import { computed } from "mobx";
import {
  ExtendedModel,
  getRootStore,
  isoStringToDateTransform,
  Model,
  tProp,
  types,
} from "mobx-keystone";
import { TModel } from "../../lib/models/t-model";
import { NudgeChannel } from "../enums";
import { nudgeNS } from "./ns.nudge";
import type RootStore from "../../root";
import type { PaymentModel } from "../payment/model.payment";
import type { ServiceAccountModel } from "../service-account/model.service-account";

@nudgeNS.model
export class NudgeModel extends ExtendedModel(TModel, {
  type: tProp(types.string),
  message: tProp(types.string),
  read: tProp(types.boolean),
  notificationLogId: tProp(types.maybeNull(types.string)),
  source: tProp(types.maybeNull(types.string)),
  serviceAccountId: tProp(types.maybeNull(types.string)),
  paymentId: tProp(types.maybeNull(types.string)),
  channel: tProp(types.maybeNull(types.string)),
}) {
  @computed
  get payment(): PaymentModel | undefined {
    const root = getRootStore<RootStore>(this);
    if (root && this.paymentId) {
      return root.tilliX.payments.modelRef(this.paymentId).maybeCurrent;
    }
    // biome-ignore lint/nursery/noUselessUndefined: <return undefined for getter>
    return undefined;
  }

  @computed
  get serviceAccount(): ServiceAccountModel | undefined {
    const root = getRootStore<RootStore>(this);
    if (root && this.serviceAccountId) {
      return root.tilliX.serviceAccounts.modelRef(this.serviceAccountId)
        .maybeCurrent;
    }
    // biome-ignore lint/nursery/noUselessUndefined: <return undefined for getter>
    return undefined;
  }
}

@nudgeNS.etc("preferences")
export class NudgePreferencesModel extends Model({
  serviceAccountId: tProp(types.string),
  newBillAvailable: tProp(types.boolean, false),
  newBillEnabled: tProp(types.boolean),
  newBillChannels: tProp(types.array(types.enum(NudgeChannel))),
  paymentDueAvailable: tProp(types.boolean, false),
  paymentDueEnabled: tProp(types.boolean),
  paymentDueChannels: tProp(types.array(types.enum(NudgeChannel))),
  paymentReceivedAvailable: tProp(types.boolean, false),
  paymentReceivedEnabled: tProp(types.boolean),
  paymentReceivedChannels: tProp(types.array(types.enum(NudgeChannel))),
  paymentOverdueAvailable: tProp(types.boolean, false),
  paymentOverdueEnabled: tProp(types.boolean),
  paymentOverdueChannels: tProp(types.array(types.enum(NudgeChannel))),
  communicationLanguage: tProp(types.maybeNull(types.string), "en-US"),
  updatedAt: tProp(types.string).withTransform(isoStringToDateTransform()),
}) {
  @computed
  get serviceAccount(): ServiceAccountModel | undefined {
    const root = getRootStore<RootStore>(this);
    if (root) {
      const account = root.tilliX.serviceAccounts.modelRef(
        this.serviceAccountId,
      );
      return account.maybeCurrent;
    }
    // biome-ignore lint/nursery/noUselessUndefined: <return undefined for getter>
    return undefined;
  }
}

@nudgeNS.etc("utilityPreferences")
export class NudgeUtilityPreferencesModel extends Model({
  serviceAccountId: tProp(types.string),
  newMeterReadAvailable: tProp(types.boolean, false),
  newMeterReadEnabled: tProp(types.boolean),
  newMeterReadChannels: tProp(types.array(types.enum(NudgeChannel))),
  outageAlertAvailable: tProp(types.boolean, false),
  outageAlertEnabled: tProp(types.boolean),
  outageAlertChannels: tProp(types.array(types.enum(NudgeChannel))),
  updatedAt: tProp(types.string).withTransform(isoStringToDateTransform()),
}) {}
